/**
 * Analytics API client — domain-specific functions for the Analytics module.
 * Covers dashboard CRUD, shared dashboards, and dashboard creation.
 */

import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AnalyticsDashboard {
  readonly dashboardId: string;
  readonly title: string;
  readonly visibility: 'private' | 'public-read';
  readonly widgetLayout: WidgetLayoutEntry[];
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly version: number;
}

export interface WidgetLayoutEntry {
  readonly widgetId: string;
  readonly col: number;
  readonly row: number;
  readonly width: number;
  readonly height: number;
}

export interface SharedDashboard extends AnalyticsDashboard {
  readonly ownerDisplayName: string;
}

export interface DashboardListResponse {
  readonly dashboards: AnalyticsDashboard[];
  readonly nextCursor: string | null;
}

export interface SharedDashboardListResponse {
  readonly dashboards: SharedDashboard[];
  readonly nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Dashboard Endpoints
// ---------------------------------------------------------------------------

/**
 * Lists dashboards owned by the current user.
 * Supports cursor-based pagination with a default page size of 20.
 */
export async function listMyDashboards(
  cursor?: string,
  limit: number = 20
): Promise<DashboardListResponse> {
  const query: Record<string, string> = { limit: String(limit) };
  if (cursor) {
    query.cursor = cursor;
  }
  const response = await api.get<{ data: AnalyticsDashboard[]; pagination: { nextCursor: string | null } }>('/v1/analytics/dashboards', query);
  return {
    dashboards: response.data ?? [],
    nextCursor: response.pagination?.nextCursor ?? null,
  };
}

/**
 * Lists public-read dashboards created by other users.
 * Supports cursor-based pagination with a default page size of 20.
 */
export async function listSharedDashboards(
  cursor?: string,
  limit: number = 20
): Promise<SharedDashboardListResponse> {
  const query: Record<string, string> = { limit: String(limit) };
  if (cursor) {
    query.cursor = cursor;
  }
  const response = await api.get<{ data: SharedDashboard[]; pagination: { nextCursor: string | null } }>('/v1/analytics/dashboards/shared', query);
  return {
    dashboards: response.data ?? [],
    nextCursor: response.pagination?.nextCursor ?? null,
  };
}

/**
 * Creates a new dashboard with default title "Untitled Dashboard" and private visibility.
 */
export async function createDashboard(
  body?: { title?: string; visibility?: 'private' | 'public-read' }
): Promise<AnalyticsDashboard> {
  const response = await api.post<{ data: AnalyticsDashboard }>('/v1/analytics/dashboards', body ?? {});
  return response.data;
}
